import type { ReactNode } from 'react'
import { animate, motion, useMotionValue, useTransform } from 'framer-motion'

export interface AnimateDigitProps extends CommonProps {
  /**
   * 初始数值
   * @default 0
   */
  init?: number | string
  /**
   * 目标数值
   */
  value: UnDef<number | string>
  /**
   * 小数位数 当为auto 时，与目标值的小数位数同步
   * @default auto
   */
  fraction?: number | 'auto'
  /**
   * 是否使用本地化
   * @default false
   */
  locale?: boolean | Intl.LocalesArgument
  /**
   * 延迟(s)
   * @default 0.2
   */
  delay?: number
  /**
   * 动画持续时间(s)
   * @default 1
   */
  duration?: number
  /**
   * 数据为 falsy 时的回退
   * @default '-'
   */
  fallback?: string
  /**
   * 前缀
   * @example <span>¥</span>
   */
  prefix?: ReactNode
  /**
   * 后缀
   * @example <span>%</span>
   */
  suffix?: ReactNode
  /**
   * 是否显示 0 或 '0'
   * @default true
   */
  showZero?: boolean
}

const AnimateDigit = ({
  init = 0,
  value,
  fraction = 'auto',
  locale = false,
  delay = 0.2,
  duration = 0.5,
  className,
  style,
  fallback = '-',
  prefix = '',
  suffix = '',
  showZero = true,
}: AnimateDigitProps) => {
  const getFraction = useCallback(
    (_fraction: number | 'auto') => {
      if (_fraction === 'auto') {
        return value?.toString().split('.')[1]?.length || 0
      }
      return _fraction as number
    },
    [value]
  )

  const isFalsy = useCallback(
    (v: typeof value) => !isDef(v) || v === '' || !Number.isFinite(Number(v)) || (!showZero && Number(v) === 0),
    []
  )

  const count = useMotionValue(init)

  const rounded = useTransform(count, latest => {
    const str = Number(latest).toFixed(getFraction(fraction))
    return locale ? Number(str).toLocaleString(typeof locale === 'string' ? locale : 'en-US') : str
  })

  useEffect(() => {
    if (isFalsy(value)) return

    const controls = animate(count, Number(value), { delay, duration })
    return () => {
      controls.stop()
    }
  }, [value])

  return (
    <>
      {prefix}
      <motion.span className={clsx('font-digit', className)} style={{ ...style }}>
        {isFalsy(value) ? fallback : rounded}
      </motion.span>
      <span>{suffix}</span>
    </>
  )
}

export default AnimateDigit
